import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  Video, 
  Clock, 
  Calendar,
  User,
  Trash2,
  AlertTriangle,
  Loader2,
  CheckCircle,
  Shield
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const VideoCallHistory = ({ userRole, showNotification }) => {
  const [videoCallHistory, setVideoCallHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Safe notification wrapper
  const notify = (message, type = 'info') => {
    if (typeof showNotification === 'function') {
      showNotification(message, type);
    } else {
      console.log(`[VideoCallHistory] Notification (${type}):`, message);
    }
  };

  useEffect(() => {
    fetchVideoCallHistory();
  }, []);

  const fetchVideoCallHistory = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      
      if (!token) {
        console.warn('[VideoCallHistory] No authentication token found');
        notify('Please login to view video call history', 'info');
        setLoading(false);
        return;
      }
      
      console.log('[VideoCallHistory] Fetching history from:', `${API}/video-calls/history`);
      
      const response = await axios.get(`${API}/video-calls/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[VideoCallHistory] Response received:', response.status);
      
      // Handle response
      if (response.status === 200) {
        const data = response.data || [];
        console.log('[VideoCallHistory] Video calls found:', data.length);
        setVideoCallHistory(data);
      } else {
        console.warn('[VideoCallHistory] Unexpected status:', response.status);
        setVideoCallHistory([]);
      }
    } catch (error) {
      console.error('[VideoCallHistory] Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      
      // Only show error notification for actual errors, not for empty results
      if (error.response?.status === 401) {
        notify('Session expired. Please login again.', 'error');
      } else if (error.response?.status === 403) {
        notify('Access denied to video call history', 'error');
      } else if (error.response?.status >= 500) {
        notify('Server error loading video call history', 'error');
      } else if (!error.response) {
        notify('Network error. Please check your connection.', 'error');
      } else {
        // For other errors, just log but don't show notification
        console.error('[VideoCallHistory] Failed to load, showing empty state');
      }
      
      setVideoCallHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHistory = async () => {
    try {
      setDeleting(true);
      const token = sessionStorage.getItem('token');
      const response = await axios.delete(`${API}/video-calls/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      notify(response.data.message || 'Video call history deleted successfully', 'success');
      setVideoCallHistory([]);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting video call history:', error);
      notify('Failed to delete video call history', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'completed': 'bg-green-100 text-green-800',
      'active': 'bg-blue-100 text-blue-800',
      'initiated': 'bg-yellow-100 text-yellow-800',
      'failed': 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2">Loading video call history...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* DPDP Transparency Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-blue-900 mb-1">🔒 DPDP Data Privacy Notice</p>
            <p className="text-blue-800">
              This history shows only call metadata (date, duration, participants). Video/audio content is NEVER recorded or stored. 
              <strong> All logs are automatically deleted after 90 days</strong> per DPDP 2023 compliance. 
              You can manually delete your history anytime using the button below.
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-blue-600" />
              Video Call History
            </CardTitle>
            {videoCallHistory.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete History
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Privacy Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-blue-900 mb-1">Privacy Protection</p>
                <p className="text-blue-700">
                  We store ONLY metadata (timestamps, duration, consent records). 
                  <strong> We do NOT store video/audio recordings or call content.</strong> 
                  All calls are end-to-end encrypted. Records are automatically deleted after 5 years.
                </p>
              </div>
            </div>
          </div>

          {/* Video Call History List */}
          {videoCallHistory.length === 0 ? (
            <div className="text-center py-12">
              <Video className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No video call history</p>
              <p className="text-gray-400 text-sm mt-2">
                Your video consultation records will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {videoCallHistory.map((call) => (
                <div
                  key={call.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      {/* Participant Info */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium">
                            {userRole === 'client' 
                              ? `with ${call.psychologist_name || 'Psychologist'}` 
                              : `with ${call.client_name || 'Client'}`}
                          </span>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(call.status)}`}>
                          {call.status}
                        </span>
                      </div>

                      {/* Date and Duration */}
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(call.started_at)}</span>
                        </div>
                        {call.duration_minutes && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{formatDuration(call.duration_minutes)}</span>
                          </div>
                        )}
                      </div>

                      {/* Consent Info */}
                      {call.consent_data && (
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          <span>Consent recorded</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <AlertDialogTitle>Delete Video Call History?</AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              This will permanently delete all your video call records including:
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Call timestamps and duration</li>
                <li>Consent records</li>
                <li>Participant information</li>
              </ul>
              <p className="mt-3 font-semibold text-gray-900">
                This action cannot be undone. Are you sure?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteHistory}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete History
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VideoCallHistory;
